/**
 * Whether "her zone" — the full-screen visit the calorie ring morphs into —
 * is on screen right now, and which day it was opened for.
 *
 * Four phases rather than a boolean because the morph itself needs to be
 * legible as a state, not just an effect: `HerZone` reads `entering` to run
 * the ring-to-halo choreography, `open` once it has settled and the apartment
 * content is live, and `leaving` to run the reverse choreography before the
 * component actually unmounts. `closed` is the only phase nothing is
 * mounted for — see `Today.tsx`, which mounts `HerZone` for every other one.
 *
 * `closeZone` is the odd one out: it lands on `closed` from any phase,
 * unconditionally, with no morph. Switching tabs mid-visit is a cut, not a
 * goodbye — see `Shell.tsx`'s `goTo`, the only caller that reaches for it
 * instead of `beginCloseZone`.
 */

import { create } from 'zustand'

export type ZonePhase = 'closed' | 'entering' | 'open' | 'leaving'

/** Which of her other rooms is open above the zone, or none. */
export type ZoneDoor = 'dossier' | 'promises' | 'trophies'

interface ZoneState {
  phase: ZonePhase
  /** The date (YYYY-MM-DD) the visit was opened for, or null while closed. */
  openedDate: string | null
  /**
   * The door row lives in `shell/Shell.tsx`, standing in for the tab bar
   * itself rather than as a fixed-position overlay — the tab bar has never
   * had a Safari viewport bug, and a normal flow element can't grow one. That
   * split (trigger in the shell, sheet in `HerZone.tsx`) needs a shared place
   * to say which sheet, if any, is open; this is it.
   */
  doorOpen: ZoneDoor | null
}

export const useZoneState = create<ZoneState>(() => ({
  phase: 'closed',
  openedDate: null,
  doorOpen: null,
}))

/** Read once, outside React — `Shell.tsx`'s tab switch is not a render. */
export function currentZonePhase(): ZonePhase {
  return useZoneState.getState().phase
}

/** closed -> entering. Ignored if a visit is already up in some phase. */
export function openZone(date: string): void {
  if (useZoneState.getState().phase !== 'closed') return
  useZoneState.setState({ phase: 'entering', openedDate: date })
}

/** entering -> open. Called once the enter morph finishes or is skipped. */
export function zoneSettled(): void {
  if (useZoneState.getState().phase !== 'entering') return
  useZoneState.setState({ phase: 'open' })
}

/** open | entering -> leaving. Starts the reverse morph; Back and the close
 * button both funnel here, never straight to `closeZone`. */
export function beginCloseZone(): void {
  const { phase } = useZoneState.getState()
  if (phase !== 'open' && phase !== 'entering') return
  useZoneState.setState({ phase: 'leaving' })
}

/** -> closed, from anywhere, with no morph. The hard exit. */
export function closeZone(): void {
  useZoneState.setState({ phase: 'closed', openedDate: null, doorOpen: null })
}

/** Opens one of her other rooms above the zone. */
export function openDoor(door: ZoneDoor): void {
  useZoneState.setState({ doorOpen: door })
}

/** Closes whichever door is open, if any. */
export function closeDoor(): void {
  useZoneState.setState({ doorOpen: null })
}
